/**
 * Building info — filled out once per building (spec §3.1) — and the machine
 * room slots the rest of the survey hangs off (mockup screen 2).
 *
 * Two jobs, in the order the surveyor does them.
 *
 * **B1–B17** are rendered by `QuestionList`, the same renderer every section
 * screen uses. Nothing about them is special-cased here: B8 opening the
 * consultant details, B13 opening the ATS block, and B14/B17 arriving as
 * "⚙ calculated for you" chips with no control (DECISIONS D-003) are all the
 * catalog and the engines doing their job through that one component.
 *
 * **Fire Life Safety is NOT here (Amendment 6, 2026-08-18 / 19).** Amendment 5
 * filed F1–F5 and the two FACP shots on the building section, and Chip's field
 * test said why that was wrong: this screen is the truck at the START of the
 * job, and the panel and its sticker are the last thing a surveyor stands in
 * front of. They have a section of their own now, listed at the foot of every
 * room's hall indicator after Access, and the walk gate waits for it. The first
 * cut of that change put a row for it HERE, under the machine rooms; the design
 * authority's answer was that it belongs nowhere on this screen — *"a survey
 * section that needs to be filled out just like any other"* — so nothing of it
 * is rendered here. Not a row, not a badge.
 *
 * What IS worth knowing about this screen: it has no notes field for a
 * blocked flag — that lives at the foot of every section screen since D-033
 * R15, and this is not one — because nothing about a building can be
 * "blocked by a locked door", and it carries no recommended shots at all.
 * The one photo tray it has
 * appears ONLY on a record that already holds building-level photos — the home
 * of a panel photo taken here before Amendment 6 (123 Smith Street), so it stays
 * visible and deletable (Chip's third finding) — and on every other survey it is
 * not there: photographs of a building price nothing, the machine rooms and
 * their components are what the office needs.
 *
 * **The machine room slots** are this screen's own, and **B12 is the only
 * control that creates or removes one**. The slot list is the rooms that exist
 * plus a slot for each one B12 still expects; there is no "+ Add a machine
 * room" button, because a screen that asks "how many?" and then also offers
 * "add one more" has two controls for one fact and no answer to which of them
 * wins (feedback b, 2026-08-06). Lowering B12 past a room that holds work asks
 * first — see `roomCountPlan`.
 *
 * **The unit cross-check waits its turn (D-014).** Specification §4 rule 1 is
 * still the engine's, still a gap at submit, and still rendered verbatim when it
 * speaks. But while the surveyor is still being asked for the car lists it
 * compares, it reads as calm progress, and before any car exists at all it says
 * nothing: "your room surveys account for 0 of 1 elevator" is not a finding, and
 * a surveyor who meets it on their second tap reads it as having broken
 * something.
 *
 * **Nothing here is a hard blocker.** A survey may arrive as bare as an address
 * (spec §2.1): no B11, no B12, no rooms. That state renders as an invitation,
 * not as an error — the gaps that matter are collected at submit, by the gap
 * engine, not by a screen refusing to move on.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Card, ConfirmPanel, MathChip, QuestionList, type GuardVerdict } from '../components';
import { sectionInstanceId, type AnswerValue } from '../../core/types';
import { hrefFor } from '../routing';
import { navigate } from '../useRoute';
import { guidePlan } from '../state/guide';
import { useSurvey } from '../state/SurveyProvider';
import { forgetWalkedUnder, useWalkedSections } from '../state/walked';
import {
  applyRoomCount,
  crossCheckView,
  forgetRefusedRoomCount,
  holdRefusedRoomCount,
  nextRoomId,
  roomCountPlan,
  takeRefusedRoomCount,
} from '../state/buildingSetup';

export interface BuildingScreenProps {
  surveyId: string;
}

export function BuildingScreen({ surveyId }: BuildingScreenProps): ReactNode {
  const { doc, results, setStructure, readOnly } = useSurvey();
  const walked = useWalkedSections(surveyId);

  /* The B12 value the surveyor asked for that would cost a room survey. Held
   * here, unwritten, until they say yes — the stepper reads the document, so
   * the number visibly stays where it was while the question is on screen. */
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  /* D-056: a lower B12 refused as its stepper unmounted (the surveyor left
   * this screen with the keyboard up) could not be asked about — this screen
   * was already gone. The guard held it; ask about it now. On mount only:
   * the doc read here is the one the screen opened on. The confirm renders
   * under the whole question list, below the fold at 430x932, and an ask the
   * surveyor never scrolls to is still a silent drop — so this one is
   * brought into view. Centred, not 'start': the top bar is sticky at top 0
   * and would sit over the confirm's title. */
  const [askedOnArrival, setAskedOnArrival] = useState(false);
  const askAnchor = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    const held = takeRefusedRoomCount(surveyId, doc);
    if (held === null) return;
    setPendingCount(held);
    setAskedOnArrival(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on arrival
  }, [surveyId]);
  useEffect(() => {
    if (askedOnArrival) askAnchor.current?.scrollIntoView({ block: 'center' });
  }, [askedOnArrival]);

  const buildingSection = results.sectionById.get(sectionInstanceId('building'));
  const check = crossCheckView(results);
  const countPlan = pendingCount === null ? null : roomCountPlan(doc, pendingCount);

  // While the building declares rooms that do not exist yet, starting the
  // next one is the walk's job HERE — this is the screen rooms are started
  // from, and the slot list that used to carry that is gone (D-029 R7).
  const plan = guidePlan(results, walked);
  const stepIsHere = plan.step.kind === 'addRoom' || plan.pendingRooms > 0;

  // The walk order's next stop after Building — the same engine list every
  // section screen walks (D-031: one linear walk, no wizard jumps).
  const buildingIndex = results.sections.findIndex(
    (candidate) => candidate.id === sectionInstanceId('building'),
  );
  const nextWalkSection = buildingIndex >= 0 ? results.sections[buildingIndex + 1] : undefined;
  const nextStop = nextWalkSection
    ? {
        title: nextWalkSection.title,
        href: hrefFor({ name: 'section', surveyId, sectionId: nextWalkSection.id }),
      }
    : undefined;

  /*
   * B12 owns the room list, so every change to it goes through `applyRoomCount`
   * once it would remove a room — silently when that room holds nothing, and
   * after a confirmation when it holds work. Raising the count, or lowering it
   * to a number the rooms already fit inside, is an ordinary answer and falls
   * straight through: `'write'`. Dropping a room with work in it asks first —
   * nothing is written yet, `'refused'`. Dropping only empty slots writes
   * immediately, itself, right here — `'written'`, so `QuestionList` knows not
   * to write it again.
   */
  const guardRoomCount = (questionId: string, next: AnswerValue): GuardVerdict => {
    if (questionId !== 'B12' || next.kind !== 'number') return 'write';
    const wanted = Math.max(0, Math.floor(next.value));
    if (wanted >= doc.rooms.length) return 'write';

    const nextPlan = roomCountPlan(doc, wanted);
    if (nextPlan.destructive) {
      setPendingCount(wanted);
      // Also held past this screen: when the refusal comes from an unmount,
      // the state above is gone with it (D-056).
      holdRefusedRoomCount(surveyId, wanted);
      return 'refused';
    }
    // Nothing to lose: the slots being dropped are rooms with a name and a type
    // and nothing else. Asking about those would be the alarm-for-nothing this
    // screen exists to stop.
    void setStructure((document) => {
      applyRoomCount(document, wanted);
    });
    // The document is clear (`applyRoomCount`); the device's own memory of
    // having walked these rooms is not, and a re-raised B12 can hand the same
    // `mr-slot-N` id to a new room (final review, F1) — forget it too.
    for (const discard of nextPlan.discards) forgetWalkedUnder(surveyId, discard.roomId);
    return 'written';
  };

  /**
   * The room id is the first free SLOT (`nextRoomId`, D-037) — the same id the
   * map's own pending row for it already opens, so the walk's button and the
   * map's row are two doors onto one Room Setup. The room itself is still
   * created on that screen, by the M1 tap that chooses its type.
   *
   * `MachineRoom.type` has no "unset" state — it decides the whole question set
   * — so creating the room first would mean guessing Traction on the surveyor's
   * behalf and showing M1 as already answered. Minting only the id keeps
   * "room type — answer first" (spec §3.2) literally true, and a surveyor who
   * backs out leaves nothing behind.
   */
  const startRoom = (): void => {
    navigate({ name: 'roomSetup', surveyId, roomId: nextRoomId(doc) });
  };

  return (
    <>
      {buildingSection ? (
        <QuestionList section={buildingSection} guard={guardRoomCount} />
      ) : (
        <Card>
          <p className="ruleline" data-legibility="helper">This survey has no building section — that should not happen.</p>
        </Card>
      )}

      {/* A real consequence of a real action, asked at the moment the action is
       * taken — which is the one time a warning belongs (D-014). */}
      {countPlan && askedOnArrival ? <span ref={askAnchor} aria-hidden="true" /> : null}
      {countPlan ? (
        <ConfirmPanel
          title={countPlan.title}
          body={countPlan.body}
          confirmLabel={countPlan.confirmLabel}
          cancelLabel={countPlan.cancelLabel}
          onConfirm={() => {
            const wanted = countPlan.count;
            const discards = countPlan.discards;
            setPendingCount(null);
            setAskedOnArrival(false);
            forgetRefusedRoomCount(surveyId);
            void setStructure((document) => {
              // Recomputed inside the transaction against the document as it is
              // at write time: the list above is what the surveyor was shown,
              // not what authorises the delete.
              applyRoomCount(document, wanted);
            });
            // Same reasoning as the non-destructive path above: the document is
            // clear, the device's own walked trail is not (final review, F1).
            for (const discard of discards) forgetWalkedUnder(surveyId, discard.roomId);
          }}
          onCancel={() => {
            setPendingCount(null);
            setAskedOnArrival(false);
            forgetRefusedRoomCount(surveyId);
          }}
        />
      ) : null}

      {/* §4 rule 1 — but only once it is settled AND failing (D-029 R4): the
       * running progress commentary and the green confirmation
       * are gone. The engine's own sentence, in red, is all the chip says. */}
      {check ? (
        <MathChip tone={check.tone} {...(check.why ? { why: check.why } : {})}>
          {check.message}
        </MathChip>
      ) : null}

      {/* The walk, in flow — LINEAR since D-031: Building is the head of the
       * walk and its next is the walk order's next stop, exactly as the
       * section screens' is. (D-029's wizard link here was a third path — it
       * could jump to Fire Life Safety or Close & submit — and R7 allows two.
       * The map is the jump.) The one override stands: while the building
       * declares rooms that do not exist, the button CREATES the next one.
       *
       * Round 7 task 10, ruling 12, fix round 1: read-only closes the WRITE
       * door — "Start Survey" would mint a room the office never asked for —
       * never the walk itself. "Close & next" is plain navigation, the same
       * live link every section screen keeps under `readOnly` (WalkButtons is
       * untouched); hiding it here too would strand a surveyor who opened a
       * submitted survey from Home with no way to look at Room 1 without the
       * map. Only the override — Start Survey — is read-only's business. */}
      <div className="walk">
        {stepIsHere && !readOnly ? (
          <button type="button" className="btn btn--primary" onClick={startRoom}>
            Start Survey
          </button>
        ) : nextStop ? (
          <a
            className="btn btn--primary btn--stack"
            href={nextStop.href}
            aria-label={`Close & next: ${nextStop.title}`}
          >
            <span className="btn__face">
              Close &amp; next
              <span className="btn__glyph" aria-hidden="true">
                ▸
              </span>
            </span>
            <span className="btn__dest">{nextStop.title}</span>
          </a>
        ) : null}
      </div>

      {/* Save backup and Discard left this screen in round 6 (D-037): the
       * Home row's swipe rail (D-035) and the close screen carry both. */}
    </>
  );
}

